import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { LoyaltyTier } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import {
  CREATE_LOYALTY_TIER_MUTATION,
  LOYALTY_TIERS_QUERY,
  UPDATE_LOYALTY_TIER_MUTATION,
} from "../../../../../graphql/loyalty";

const createTierFormSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .min(2, t("validation.tierNameRequired"))
      .max(50, t("validation.tierNameMaxLength")),
    description: z.string().max(500, t("validation.tierDescriptionMaxLength")).optional(),
    minPoints: z.number().min(0, t("validation.minPointsPositive")),
    maxPoints: z.number().min(0, t("validation.maxPointsPositive")).nullable().optional(),
    color: z.string().regex(/^#[0-9A-F]{6}$/i, t("validation.validHexColor")),
    priorityBooking: z.boolean(),
  });

type TierFormValues = z.infer<ReturnType<typeof createTierFormSchema>>;

interface TierFormDialogProps {
  tier?: LoyaltyTier | null;
  open: boolean;
  onClose: () => void;
}

export function TierFormDialog({ tier, open, onClose }: TierFormDialogProps) {
  const { t } = useTranslation("settings");
  const { currentSalon } = useSalonStore();
  const isEditing = tier?.id;

  const tierFormSchema = createTierFormSchema(t);
  const form = useForm<TierFormValues>({
    resolver: zodResolver(tierFormSchema),
    defaultValues: {
      name: "",
      description: "",
      minPoints: 0,
      maxPoints: null,
      color: "#6B7280",
      priorityBooking: false,
    },
  });

  const [createTier, { loading: creating }] = useMutation(CREATE_LOYALTY_TIER_MUTATION, {
    refetchQueries: [
      { query: LOYALTY_TIERS_QUERY, variables: { franchiseId: currentSalon?.franchiseId } },
    ],
  });

  const [updateTier, { loading: updating }] = useMutation(UPDATE_LOYALTY_TIER_MUTATION, {
    refetchQueries: [
      { query: LOYALTY_TIERS_QUERY, variables: { franchiseId: currentSalon?.franchiseId } },
    ],
  });

  const loading = creating || updating;

  useEffect(() => {
    if (tier?.id) {
      form.reset({
        name: tier.name,
        description: tier.description || "",
        minPoints: tier.minPoints,
        maxPoints: tier.maxPoints,
        color: tier.color,
        priorityBooking: tier.priorityBooking,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        minPoints: 0,
        maxPoints: null,
        color: "#6B7280",
        priorityBooking: false,
      });
    }
  }, [tier, form]);

  const onSubmit = async (values: TierFormValues) => {
    if (!currentSalon?.franchiseId) return;

    try {
      const input = {
        name: values.name,
        description: values.description || undefined,
        minPoints: values.minPoints,
        maxPoints: values.maxPoints || undefined,
        color: values.color,
        priorityBooking: values.priorityBooking,
      };

      if (isEditing) {
        await updateTier({
          variables: {
            id: tier.id,
            input,
          },
        });
      } else {
        await createTier({
          variables: {
            franchiseId: currentSalon.franchiseId,
            input,
          },
        });
      }

      onClose();
    } catch (error) {
      console.error("Failed to save tier:", error);
    }
  };

  const colorOptions = [
    { name: t("rewards.tierManagement.colors.gray"), value: "#6B7280" },
    { name: t("rewards.tierManagement.colors.bronze"), value: "#CD7F32" },
    { name: t("rewards.tierManagement.colors.silver"), value: "#C0C0C0" },
    { name: t("rewards.tierManagement.colors.gold"), value: "#FFD700" },
    { name: t("rewards.tierManagement.colors.platinum"), value: "#E5E4E2" },
    { name: t("rewards.tierManagement.colors.diamond"), value: "#B9F2FF" },
    { name: t("rewards.tierManagement.colors.blue"), value: "#3B82F6" },
    { name: t("rewards.tierManagement.colors.purple"), value: "#8B5CF6" },
    { name: t("rewards.tierManagement.colors.green"), value: "#10B981" },
    { name: t("rewards.tierManagement.colors.red"), value: "#EF4444" },
  ];

  return (
    <Dialog open={open} onOpenChange={() => !loading && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("dialogs.editLoyaltyTier") : t("dialogs.createLoyaltyTier")}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("labels.tierName")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("placeholders.tierNameExample")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("labels.tierColor")}</FormLabel>
                    <div className="grid grid-cols-5 gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          className={`w-8 h-8 rounded border-2 ${
                            field.value === color.value ? "border-primary" : "border-gray-300"
                          }`}
                          style={{ backgroundColor: color.value }}
                          onClick={() => field.onChange(color.value)}
                          title={color.name}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("labels.tierDescription")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("placeholders.enterTierDescription")}
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="minPoints"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("labels.minPoints")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="0"
                        {...field}
                        onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxPoints"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("labels.maxPoints")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder={t("placeholders.leaveEmptyUnlimited")}
                        {...field}
                        value={field.value || ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number.parseInt(e.target.value, 10) : null
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="priorityBooking"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("rewards.tierManagement.priorityBooking")}
                    </FormLabel>
                    <div className="text-sm text-muted-foreground">
                      {t("descriptions.priorityBookingInfo")}
                    </div>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                {t("actions.cancel")}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? t("status.saving")
                  : isEditing
                    ? t("actions.updateTier")
                    : t("actions.createTier")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
